/**
 * Trimmer arithmetic, extracted from the Filmstrip so it can be tested.
 *
 * Clamping bugs here are silent: a handle that crosses its partner or slides
 * past the end produces a nonsense range rather than an error, and the first
 * sign of it is a wrong export.
 */

export type Range = { start: number; end: number };

/**
 * Handles may never be closer than this. Two frames, or half a second on
 * slow-frame-rate sources, whichever is larger.
 */
export const minGapFor = (fps: number): number => Math.max(0.5, 2 / fps);

export const snapToFrame = (seconds: number, fps: number): number =>
  Math.round(seconds * fps) / fps;

/** Pointer position → seconds on the timeline, snapped to a frame boundary. */
export const secondsAtX = (opts: {
  clientX: number;
  boxLeft: number;
  boxWidth: number;
  durationInSeconds: number;
  fps: number;
}): number => {
  if (opts.boxWidth <= 0 || opts.durationInSeconds <= 0) return 0;
  const ratio = Math.min(Math.max((opts.clientX - opts.boxLeft) / opts.boxWidth, 0), 1);
  return snapToFrame(ratio * opts.durationInSeconds, opts.fps);
};

export const percentOf = (seconds: number, durationInSeconds: number): number =>
  durationInSeconds > 0
    ? Math.min(Math.max((seconds / durationInSeconds) * 100, 0), 100)
    : 0;

/**
 * Every mutation below funnels through this, so no caller can produce a range
 * that is inverted, negative, past the end, or narrower than the gap.
 */
const clampRange = (range: Range, durationInSeconds: number, minGap: number): Range => {
  // A clip shorter than the gap cannot satisfy it; use the whole thing.
  if (durationInSeconds <= minGap) return { start: 0, end: durationInSeconds };

  const start = Math.min(Math.max(range.start, 0), durationInSeconds - minGap);
  const end = Math.min(Math.max(range.end, start + minGap), durationInSeconds);
  return { start: Math.min(start, end - minGap), end };
};

export const moveStart = (
  range: Range,
  to: number,
  durationInSeconds: number,
  minGap: number,
): Range => clampRange({ start: to, end: range.end }, durationInSeconds, minGap);

export const moveEnd = (
  range: Range,
  to: number,
  durationInSeconds: number,
  minGap: number,
): Range => clampRange({ start: range.start, end: to }, durationInSeconds, minGap);

/** Slide the whole selection, preserving its width. */
export const moveRegion = (
  range: Range,
  nextStart: number,
  durationInSeconds: number,
): Range => {
  const span = range.end - range.start;
  const start = Math.min(Math.max(nextStart, 0), Math.max(durationInSeconds - span, 0));
  return { start, end: Math.min(start + span, durationInSeconds) };
};

export const nudgeHandle = (
  range: Range,
  which: 'start' | 'end',
  deltaSeconds: number,
  durationInSeconds: number,
  minGap: number,
): Range =>
  which === 'start'
    ? moveStart(range, range.start + deltaSeconds, durationInSeconds, minGap)
    : moveEnd(range, range.end + deltaSeconds, durationInSeconds, minGap);

/**
 * Seconds → a frame the Player can actually seek to.
 *
 * The last valid frame is `durationInFrames - 1`, so a timestamp at the very
 * end rounds one frame past the composition. Caption timestamps are the real
 * hazard: unlike a pointer position they are not derived from the timeline at
 * all, and a range offset, the global sync control, or Whisper's chunked
 * output can each push one beyond the video. Seeking out of range leaves the
 * video element stuck on its last frame while the composition keeps running.
 */
export const seekFrameFor = (
  seconds: number,
  fps: number,
  durationInFrames: number,
): number => {
  const frame = Math.round(seconds * fps);
  if (!Number.isFinite(frame)) return 0;
  return Math.min(Math.max(frame, 0), Math.max(durationInFrames - 1, 0));
};

